import { useEffect, useRef, useState } from 'react';

// Détecte l'inactivité utilisateur et déclenche un avertissement puis un logout.
//
// Paramètres :
// - timeoutMs : durée totale avant logout (défaut 30 min)
// - warningMs : avertissement X ms avant le logout (défaut 1 min)
// - onLogout  : callback déclenché à la fin du timer
// - onWarning : callback déclenché à warningMs avant le logout (peut afficher un modal)
//
// Réinitialise le compteur sur : mousemove, mousedown, keydown, touchstart, scroll, click.
// Important pour les postes partagés en agence où une session laissée ouverte
// est un risque (un employé pourrait voir des données qui ne lui appartiennent pas).
const DEFAULT_TIMEOUT = 30 * 60 * 1000; // 30 min
const DEFAULT_WARNING = 60 * 1000;       // 1 min

const useIdleLogout = ({ timeoutMs = DEFAULT_TIMEOUT, warningMs = DEFAULT_WARNING, onLogout, onWarning, onActivity } = {}) => {
    const logoutTimer = useRef(null);
    const warningTimer = useRef(null);
    const [warningActive, setWarningActive] = useState(false);

    const clearTimers = () => {
        if (logoutTimer.current) clearTimeout(logoutTimer.current);
        if (warningTimer.current) clearTimeout(warningTimer.current);
        logoutTimer.current = null;
        warningTimer.current = null;
    };

    const reset = () => {
        clearTimers();
        setWarningActive(false);
        onActivity?.();
        warningTimer.current = setTimeout(() => {
            setWarningActive(true);
            onWarning?.();
        }, Math.max(0, timeoutMs - warningMs));
        logoutTimer.current = setTimeout(() => {
            onLogout?.();
        }, timeoutMs);
    };

    useEffect(() => {
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
        const handler = () => reset();
        events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
        reset();
        return () => {
            events.forEach((e) => window.removeEventListener(e, handler));
            clearTimers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { warningActive, reset };
};

export default useIdleLogout;
